import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { BookOpen, Upload, Search, Plus, Eye, FileText } from "lucide-react";
import { API_ENDPOINTS } from "../config/api";
import apiRequest from "../utils/api";
import { FileUploadModal } from "../components/FileUploadModal";
import { getCategoryLabel } from "../utils/categoryMapping";

interface Product {
  id: number;
  title: string;
  author: string;
  price: number;
  category: string;
  imageUrl: string; // API에서는 imageUrl로 옴
  description: string;
  type: string; // API에서는 type으로 옴
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export const CourseManagement: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [loading, setLoading] = useState(true);

  // 상품 목록 조회
  useEffect(() => {
    fetchProducts();
  }, []);

  // 검색 필터링
  useEffect(() => {
    const filtered = products.filter(
      (product) =>
        product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredProducts(filtered);
  }, [products, searchTerm]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(API_ENDPOINTS.PRODUCTS, {
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setProducts(data.data.content); // API 응답 구조에 맞게 수정
        }
      }
    } catch (error) {
      console.error("상품 목록 조회 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (product: Product) => {
    setSelectedProduct(product);
    setShowFileUpload(true);
  };

  const handleCloseModal = () => {
    setShowFileUpload(false);
    setSelectedProduct(null);
  };

  const handleProductClick = (productId: number) => {
    navigate(`/product/${productId}`);
  };

  const handleUploadSuccess = () => {
    // 업로드 성공 후 상품 목록 새로고침
    fetchProducts();
    handleCloseModal();
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <BookOpen className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">상품 관리</h1>
        </div>
        <Button
          className="flex items-center space-x-2"
          onClick={() => navigate("/product-register")}
        >
          <Plus className="h-4 w-4" />
          <span>새 상품 등록</span>
        </Button>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">전체 상품</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{products.length}</div>
            <p className="text-xs text-muted-foreground">등록된 상품 수</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">유료 상품</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {products.filter((p) => p.type === "PURCHASE").length}
            </div>
            <p className="text-xs text-muted-foreground">구매형 상품</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">무료 상품</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {products.filter((p) => p.type === "FREE").length}
            </div>
            <p className="text-xs text-muted-foreground">무료 상품</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">평균 가격</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {products.length > 0
                ? Math.round(
                    products.reduce((sum, p) => sum + p.price, 0) /
                      products.length
                  ).toLocaleString()
                : 0}
              원
            </div>
            <p className="text-xs text-muted-foreground">상품 평균 가격</p>
          </CardContent>
        </Card>
      </div>

      {/* 검색 및 필터 */}
      <Card>
        <CardHeader>
          <CardTitle>상품 검색</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="상품명, 저자, 카테고리로 검색..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              className="flex-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* 강좌 목록 테이블 */}
      <Card>
        <CardHeader>
          <CardTitle>상품 목록</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">로딩 중...</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">썸네일</TableHead>
                    <TableHead className="w-48">상품명</TableHead>
                    <TableHead className="w-24">저자</TableHead>
                    <TableHead className="w-32">카테고리</TableHead>
                    <TableHead className="w-24">가격</TableHead>
                    <TableHead className="w-20">타입</TableHead>
                    <TableHead className="w-24">등록일</TableHead>
                    <TableHead className="w-32">액션</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <img
                          src={product.imageUrl}
                          alt={product.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        <button
                          onClick={() => handleProductClick(product.id)}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {product.title}
                        </button>
                      </TableCell>
                      <TableCell>{product.author}</TableCell>
                      <TableCell className="w-32">
                        <Badge
                          variant="secondary"
                          className="whitespace-normal break-words text-xs max-w-full"
                        >
                          {getCategoryLabel(product.category)}
                        </Badge>
                      </TableCell>
                      <TableCell>{product.price.toLocaleString()}원</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            product.type === "PURCHASE" ? "default" : "outline"
                          }
                        >
                          {product.type === "PURCHASE" ? "유료" : "무료"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(product.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleProductClick(product.id)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            관리
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleFileUpload(product)}
                          >
                            <Upload className="h-4 w-4 mr-1" />
                            등록
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 파일 업로드 모달 */}
      {showFileUpload && selectedProduct && (
        <FileUploadModal
          product={selectedProduct}
          onClose={handleCloseModal}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
};
